import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DxSelectBoxComponent } from 'devextreme-angular';
import { Subscription, combineLatest } from 'rxjs';
import { EMPTY_FIELD_CSCA, IEidosCscaColumns, IEidosCscaFieldKey, IFieldEidosCsca } from './eidos-country-state-county-address.model';
import { ReservationApiService } from '@app/reservation/services/reservation-api.service';

@Component({
  selector: 'eidos-country-state-county-address',
  templateUrl: './eidos-country-state-county-address.component.html',
  styleUrls: ['./eidos-country-state-county-address.component.scss'],
  standalone: false,
})
export class EidosCountryStateCountyAddressComponent {
  @ViewChild('countryBox') public countryBox?: DxSelectBoxComponent;
  @ViewChild('marketBox') public marketBox?: DxSelectBoxComponent;

  protected fld: IFieldEidosCsca = structuredClone(EMPTY_FIELD_CSCA);
  protected _fields: string = "";

  public lkDs: { [key: string]: any[] } = {};
  private subscription?: Subscription;
  protected countryIso2?: string;
  protected countryIso?: string;
  private listCountry: any[] = [];
  protected stateMandatory: boolean = true;

  @Input() requiredState: boolean = false;

  @Input() disabled: boolean = false;
  @Output() disabledChange = new EventEmitter<boolean>();

  @Input() values: any = {};
  @Output() valuesChange = new EventEmitter();

  @Input() labelAddress: string = 'Search Address';

  @Input() allowZip4:boolean = false;

  @Input() mandatory: string = '';
  @Input() boolRequired: boolean = true;
  @Input() boolMarket: boolean = false;
  @Input() boolCountry: boolean = false;
  @Input() boolAddressSearch: boolean = true;
  @Input() boolAddress: boolean = true;
  @Input() boolState: boolean = false;
  @Input() boolCounty: boolean = true;
  @Input() boolCity: boolean = false;
  @Input() boolZip: boolean = false;
  @Input() boolZip4: boolean = false;

  @Input() requiredMarket: boolean = false;
  @Input() requiredCountry: boolean = false;
  @Input() requiredAddress: boolean = false;
  @Input() requiredCounty: boolean = false;
  @Input() requiredCity: boolean = false;
  @Input() requiredZip: boolean = false;
  @Input() requiredZip4: boolean = false;

  @Input() labelMarket: string = 'Market';
  @Input() labelCountry: string = 'Country';
  @Input() labelState: string = 'State';
  @Input() labelCounty: string = 'County';
  @Input() labelCity: string = 'City';
  @Input() labelZip: string = 'Zip';
  @Input() labelZip4: string = 'Zip 4';
  @Input() labelAddressInput: string = 'Address';

  @Input() rowCols: string = 'row-cols-5';

  @Input() colClasses: IEidosCscaColumns = {};

  @Input() set fields(value: string) {
    this._fields = value;

    const listElement = value.split('|');
    listElement.forEach(le => {
      const key = (le.split(':')[0] || '').toLowerCase().trim();
      const value = (le.split(':').pop() ?? '').trim();

      switch (key) {
        case 'market': this.fld.market = value; break;
        case 'country':
          this.fld.country = value;
          this.labelCountry = this.labelCountry != 'Country' ? this.labelCountry : this.formatLabel(value);
          break;
        case 'state':
          this.fld.state = value;
          this.labelState = this.labelState != 'State' ? this.labelState : this.formatLabel(value);
          break;
        case 'county':
          this.fld.county = value;
          this.labelCounty = this.labelCounty != 'County' ? this.labelCounty : this.formatLabel(value);
          break;
        case 'address':
          this.fld.address = value;
          this.labelAddressInput = this.labelAddressInput != 'Address' ? this.labelAddressInput : this.formatLabel(value);
          this.labelAddress = this.labelAddress != 'Search Address' ? this.labelAddress : 'Search ' + this.formatLabel(value);
          break;
        case 'city':
          this.fld.city = value;
          this.labelCity = this.labelCity != 'City' ? this.labelCity : this.formatLabel(value);
          break;
        case 'zip':
          this.fld.zip = value;
          this.labelZip = this.labelZip != 'Zip' ? this.labelZip : this.formatLabel(value);
          break;
        case 'zip4':
          this.allowZip4 = true;
          this.fld.zip4 = value;
          this.labelZip4 = this.labelZip4 != 'Zip 4' ? this.labelZip4 : this.formatLabel(value);
          break;
        case 'longitude':
          this.fld.longitude = value;
          break;
        case 'latitude':
          this.fld.latitude = value;
          break;
      }
    });
    this.loadValues();
  }

  //MARKET
  @Input() set market(v: string) {
    this.marketChange.emit(v);
    this.values[this.fld.market] = v;
    this.valuesChange.emit(this.values);
  } get market() {
    return this.values[this.fld.market];
  }
  @Output() marketChange = new EventEmitter<any>();

  //COUNTRY
  @Input() set country(v: string) {
    this.countryChange.emit(v);
    this.stateMandatory = (v ?? '') !== 'GBR';
    this.values[this.fld.country] = v;
    this.valuesChange.emit(this.values);
    this.retrieveCountryIsoFromValues(v);
  } get country() {
    return this.values[this.fld.country];
  }
  @Output() countryChange = new EventEmitter<any>();

  //ADDRESS
  @Input() set address(v: string) {
    this.addressChange.emit(v);
    this.values[this.fld.address] = v;
    this.valuesChange.emit(this.values);
  } get address() {
    return this.values[this.fld.address] ?? '';
  }
  @Output() addressChange = new EventEmitter<any>();

  //STATE
  @Input() set state(v: string) {
    this.stateChange.emit(v);
    this.values[this.fld.state] = v;
    this.valuesChange.emit(this.values);
  } get state() {
    return this.values[this.fld.state] ?? '';
  }
  @Output() stateChange = new EventEmitter<any>();

  //COUNTY
  @Input() set county(v: string) {
    this.countyChange.emit(v);
    this.values[this.fld.county] = v;
    this.valuesChange.emit(this.values);
  } get county() {
    return this.values[this.fld.county] ?? '';
  }
  @Output() countyChange = new EventEmitter<any>();

  //CITY
  @Input() set city(v: string) {
    this.cityChange.emit(v);
    this.values[this.fld.city] = v;
    this.valuesChange.emit(this.values);
  } get city() {
    return this.values[this.fld.city] ?? '';
  }
  @Output() cityChange = new EventEmitter<any>();

  //ZIP
  @Input() set zip(v: string) {
    this.zipChange.emit(v);
    this.values[this.fld.zip] = v;
    this.valuesChange.emit(this.values);
  } get zip() {
    return this.values[this.fld.zip] ?? '';
  }
  @Output() zipChange = new EventEmitter<any>();

  //ZIP4
  @Input() set zip4(v: string) {
    this.zip4Change.emit(v);
    this.values[this.fld.zip4] = v;
    this.valuesChange.emit(this.values);
  } get zip4() {
    return this.values[this.fld.zip4] ?? '';
  }

  @Output() zip4Change = new EventEmitter<any>();


  @Input() set boolOptions(value: string | undefined) {
    this.set_boolean_field(value);
  }


  constructor(private readonly service: ReservationApiService, private readonly cdr: ChangeDetectorRef) {
    this.initConfig();
  }

  protected getColClass(key: IEidosCscaFieldKey, fallback = 'col') {
    return this.colClasses[key] ?? fallback;
  }

  private formatLabel(input: string) {
    return input.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  private initConfig() {
    const p01 = this.service.getDynamicAPI('Country');
    const p02 = this.service.getDynamicAPI('Market/Select');
    const obs = combineLatest([p01, p02]);
    this.subscription = obs.subscribe(res => {
      this.lkDs = {};
      this.lkDs['Country'] = res[0].body.Countries;
      this.lkDs['Market'] = res[1].body.Market;
      this.listCountry = structuredClone(this.lkDs['Country']);
    });
  }

  private loadValues() {
    if (this.subscription?.closed) {
      this.market = this.values[this.fld.market];
      this.country = this.values[this.fld.country];
      this.state = this.values[this.fld.state];
      this.county = this.values[this.fld.county];
      this.address = this.values[this.fld.address];
      this.city = this.values[this.fld.city];
      this.zip = this.values[this.fld.zip];
      this.zip4 = this.values[this.fld.zip4];
    } else {
      setTimeout(() => this.loadValues(), 350);
    }
  }

  protected marketChangeEvent(e: any) {
    const item = e.component.option('selectedItem');
    if (!item) {
      this.values[this.fld.country] = '';
      this.values[this.fld.county] = '';
      this.values[this.fld.state] = '';
      this.lkDs['Country'] = structuredClone(this.listCountry);
    } else {
      const tmpCountryList = structuredClone(this.listCountry);
      this.lkDs['Country'] = tmpCountryList.filter(el => el.MarketCC === item.marketcc);
    }

    this.valuesChange.emit(this.values);
  }

  private retrieveCountryIsoFromValues(country: string) {
    const item = this.lkDs['Country'].find((el: any) => el.CountryCodISO3 === country);
    if(!item) return;
    this.countryIso = item.CountryCodISO3;
    this.countryIso2 = item.CountryCodISO2;
    this.requiredState = !!item.CountyState && item.CountryCodISO3 != 'IRL';
    this.cdr.detectChanges();
    this.loadState();
  }

  private loadState() {
    this.lkDs['State'] = [];
    if (this.requiredState) {
      this.service.getDynamicAPI('State', { CountryCodISO3: this.countryIso }).subscribe(res => {
        this.lkDs['State'] = res.body.States;
        this.cdr.detectChanges();
      });
    }
  }

  protected changeAddressEvent(e: any) {
    this.address = e.address;
    this.state = (e.state ?? '').toUpperCase();
    this.county = e.county;
    this.city = e.city;
    this.zip = e.zip;
    this.zip4 = e.zip4;
    this.values[this.fld.longitude] = e.longitude;
    this.values[this.fld.latitude] = e.latitude;
    this.valuesChange.emit(this.values);
  }


  private set_boolean_field(value: string | undefined) {
    let option_map: Map<string, boolean> = new Map();
    let boolRequired = false;
    (value ?? '').split(',').forEach(v => {
      const key = v.split(':')[0].toLowerCase().trim();
      const tmp_value = v.split(':').pop()?.toLowerCase().trim() === 'no';
      option_map.set(key, !tmp_value);
    });

    option_map.forEach((v, k) => {
      if (!boolRequired && v) boolRequired = true;
      switch (k) {
        case 'market':
          this.requiredMarket = v;
          break;
        case 'country':
          this.requiredCountry = v;
          break;
        case 'address':
          this.requiredAddress = v;
          break;
        case 'county':
          this.requiredCounty = v;
          break;
        case 'state':
          this.requiredState = v;
          break;
        case 'city':
          this.requiredCity = v;
          break;
        case 'zip':
          this.requiredZip = v;
          break;
        case 'zip4':
          this.requiredZip4 = v;
          break;
      }
    });

    this.boolRequired = boolRequired;
    this.cdr.detectChanges();
  }



}
